// WF-CONTROL (docs/design/2026-10-26-owner-workforce-plugin.md; docs/runbooks/workforce-control-plugin.md):
// Workforce Control — a company admin manages and trains the digital workforce from Claude Code.
//   - A person's Claude Code connections (`cp.control_token`): make a pairing code (shown once,
//     10 minutes, single use), list, revoke. At most 5 live per person; 90 days each. The routes
//     sit under the company the person works in (its log records `control_token.*`); a token
//     reaches every company where its person may use Workforce Control.
//   - `POST /v1/control/pair`: the code → the token (`cosk_ctl_…`), returned once. No session;
//     every failure is the same 404. `Accept: text/plain` answers the bare token (for
//     `claude mcp add … --header "Authorization: Bearer $(curl …)"`).
//   - The `workforce_plugin` add-on, switched by an owner (or the company's agency operator) from
//     Setup; off by default; requires `agents`.
//   - Profile revisions of a DE or an agent (every change to role, goal, backstory, standing
//     orders, responsibilities or skills), and whether the newest reached the runtime.
// The MCP endpoint itself (`POST /mcp/control`) is JSON-RPC, outside the registry (`/mcp/*`).
import { z } from "zod";
import { EntitlementStatus, ProfileRevisionSubject, ProfileRevisionVia } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const core = { feature: "core" } as const;
const base = "/v1/companies/{co}";

/** A connection as its owner sees it (never the token, never the code's hash). */
export const ControlToken = z
  .object({
    id: Uuid,
    label: z.string(),
    /** `pending` (code not used yet), `live`, `expired` (code or token), `revoked`. */
    state: z.enum(["pending", "live", "expired", "revoked"]),
    /** The token's first 16 characters, once paired. */
    key_prefix: z.string().nullable(),
    created_at: Timestamp,
    pair_expires_at: Timestamp.nullable(),
    paired_at: Timestamp.nullable(),
    expires_at: Timestamp,
    last_used_at: Timestamp.nullable(),
    revoked_at: Timestamp.nullable(),
    /** The person had a second factor when it was made. */
    mfa: z.boolean(),
  })
  .meta({ id: "ControlToken" });

export const ControlTokenList = z
  .object({
    data: z.array(ControlToken),
    /** Live and pending connections count against this. */
    limit: z.int().min(1),
  })
  .meta({ id: "ControlTokenList" });

export const ControlTokenCreate = z
  .strictObject({
    label: z.string().trim().min(1).max(80).describe('Where it is used ("Valentina\'s laptop")'),
    /**
     * A current code from the person's authenticator: required when they have two-factor
     * sign-in (a step-up, verified on this request; the connection records it as `mfa`).
     */
    totp_code: z
      .string()
      .length(6)
      .regex(/^\d{6}$/)
      .optional(),
  })
  .meta({ id: "ControlTokenCreate" });

/** The pairing code: shown once, never stored in clear. */
export const ControlTokenCreated = z
  .object({
    token: ControlToken,
    pair_code: z.string(),
    pair_expires_at: Timestamp,
  })
  .meta({ id: "ControlTokenCreated" });

export const ControlTokenParams = z.object({ co: Uuid, id: Uuid });

export const ControlPair = z
  .strictObject({ code: z.string().trim().min(1).max(64) })
  .meta({ id: "ControlPair" });

/** The token itself: returned by this one response only. */
export const ControlPaired = z
  .object({ token: z.string(), token_id: Uuid, expires_at: Timestamp })
  .meta({ id: "ControlPaired" });

/** The add-on as Setup shows it, and whether the caller may switch it. */
export const WorkforcePluginState = z
  .object({
    status: EntitlementStatus,
    /** `agents` is on (the add-on requires it). */
    agents_on: z.boolean(),
    /** `agents` takes changes (on and not in read-only grace): a new connection needs it. */
    agents_writable: z.boolean(),
    /** The caller is an owner, or the company's agency operator. */
    can_switch: z.boolean(),
    /** The caller may use Workforce Control here (an owner or admin, or the agency operator). */
    can_use: z.boolean(),
  })
  .meta({ id: "WorkforcePluginState" });

export const WorkforcePluginPut = z
  .strictObject({ enabled: z.boolean() })
  .meta({ id: "WorkforcePluginPut" });

export const ProfileRevision = z
  .object({
    id: Uuid,
    subject_kind: ProfileRevisionSubject,
    subject_id: Uuid,
    n: z.int().min(1),
    fields: z.record(z.string(), z.unknown()),
    changes: z.record(z.string(), z.object({ from: z.unknown(), to: z.unknown() })),
    actor: z.object({
      kind: z.string().nullable(),
      membership_id: Uuid.nullable(),
      user_id: Uuid.nullable(),
      name: z.string().nullable(),
    }),
    via: ProfileRevisionVia,
    control_token_id: Uuid.nullable(),
    synced_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ProfileRevision" });

export const ProfileRevisionPage = page(ProfileRevision)
  .extend({
    /**
     * The newest revision and whether the runtime has it. Phase 1 never syncs a revision to the
     * live Hermes profile (ClawPanel intake is phase 2): `unsynced` is true while the newest
     * revision has no `synced_at`.
     */
    runtime: z.object({
      latest_n: z.int().min(1).nullable(),
      synced_n: z.int().min(1).nullable(),
      unsynced: z.boolean(),
    }),
  })
  .meta({ id: "ProfileRevisionPage" });

export const controlRoutes = {
  "control.tokens.list": defineRoute({
    ...core,
    id: "control.tokens.list",
    method: "get",
    path: `${base}/control-tokens`,
    tag: "control",
    summary:
      "The caller's own Claude Code connections (Workforce Control; every company's), newest first",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ControlTokenList },
    errors: [],
    emits: [],
  }),
  "control.tokens.create": defineRoute({
    // A new connection needs the add-on on (gated like any add-on route); listing and revoking
    // stay core, so a person can always see and cut their connections.
    feature: "workforce_plugin",
    id: "control.tokens.create",
    method: "post",
    path: `${base}/control-tokens`,
    tag: "control",
    summary:
      "Make a pairing code for a Claude Code connection (shown once, 10 minutes, single use; at most 5 live). The add-on must be on and the caller an owner or admin here (or the agency operator)",
    access: "manager",
    params: CompanyParams,
    body: ControlTokenCreate,
    response: { status: 201, schema: ControlTokenCreated },
    errors: ["conflict", "mfa_code_required"],
    emits: ["control_token.created"],
  }),
  "control.tokens.revoke": defineRoute({
    ...core,
    id: "control.tokens.revoke",
    method: "post",
    path: `${base}/control-tokens/{id}/revoke`,
    tag: "control",
    summary: "Revoke one of the caller's own Claude Code connections (its next call is refused)",
    access: "member",
    params: ControlTokenParams,
    response: { status: 200, schema: ControlToken },
    errors: [],
    emits: ["control_token.revoked"],
  }),
  "control.pair": defineRoute({
    ...core,
    id: "control.pair",
    method: "post",
    path: "/v1/control/pair",
    tag: "control",
    summary:
      "Exchange a pairing code for the connection's token (returned once; any failure is 404)",
    description:
      '`Accept: text/plain` answers the bare token, for `claude mcp add --header "Authorization: Bearer $(curl …)"`.',
    access: "public",
    body: ControlPair,
    response: { status: 200, schema: ControlPaired },
    errors: ["not_found"],
    emits: ["control_token.paired"],
  }),
  "workforcePlugin.get": defineRoute({
    ...core,
    id: "workforcePlugin.get",
    method: "get",
    path: `${base}/workforce-plugin`,
    tag: "control",
    summary: "The Workforce Control add-on here, and whether the caller may switch or use it",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: WorkforcePluginState },
    errors: [],
    emits: [],
  }),
  "workforcePlugin.put": defineRoute({
    ...core,
    id: "workforcePlugin.put",
    method: "put",
    path: `${base}/workforce-plugin`,
    tag: "control",
    summary: "Switch the Workforce Control add-on on or off (an owner, or the agency operator)",
    access: "manager",
    params: CompanyParams,
    body: WorkforcePluginPut,
    response: { status: 200, schema: WorkforcePluginState },
    errors: ["addon_requires"],
    emits: ["company.entitlement_changed"],
  }),
  "digitalEmployees.profileRevisions": defineRoute({
    ...core,
    id: "digitalEmployees.profileRevisions",
    method: "get",
    path: `${base}/digital-employees/{id}/profile-revisions`,
    tag: "digital-employees",
    summary:
      "The DE's profile revisions, newest first (who, what changed, through what), and whether the newest reached the runtime",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: ProfileRevisionPage },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "workforceAgents.profileRevisions": defineRoute({
    ...core,
    id: "workforceAgents.profileRevisions",
    method: "get",
    path: `${base}/workforce/agents/{id}/profile-revisions`,
    tag: "workforce",
    summary: "The agent's profile revisions, newest first",
    access: "viewer",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: ProfileRevisionPage },
    errors: ["invalid_cursor"],
    emits: [],
  }),
} as const;
